import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import MoneyInput from '../components/MoneyInput';
import DeudaModal from './DeudaModal';
import { TrashIcon, EyeIcon, EyeOffIcon } from '../components/Icons';
import { onDataChanged } from '../utils/dataBus';

const formatMoneyRaw = (n) => '$' + (n || 0).toLocaleString('es-AR');
const MASK = '$ ••••';
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
const hoyStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const ESTADO_COLOR = { vigente: '#f59e0b', 'en gestión': '#3b82f6', cobrada: '#22c55e', incobrable: '#ef4444' };

export default function DeudasDrawer({ open, onClose, onSaved, initialEstado = 'activas', ocultar = false }) {
  const [deudas, setDeudas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [estadoFiltro, setEstadoFiltro] = useState('activas');
  const [toast, setToast] = useState(null);
  const [ocultarLocal, setOcultarLocal] = useState(ocultar);

  const formatMoney = (n) => ocultarLocal ? MASK : formatMoneyRaw(n);

  // pago
  const [pagoDeuda, setPagoDeuda] = useState(null);
  const [montoPago, setMontoPago] = useState('');
  const [fechaPago, setFechaPago] = useState(hoyStr());
  const [guardandoPago, setGuardandoPago] = useState(false);

  const showToast = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 2400); };

  const fetchDeudas = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('deudas')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula), deudas_pagos(*)')
      .order('created_at', { ascending: false });
    setDeudas(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchDeudas();
      setEstadoFiltro(initialEstado || 'activas');
      setOcultarLocal(ocultar);
    } else {
      setModalOpen(false);
      setEditing(null);
      setPagoDeuda(null);
    }
  }, [open, fetchDeudas, initialEstado, ocultar]);

  // Mientras está abierto, refrescar ante cualquier escritura en deudas
  // (imputar un ingreso a una deuda desde otro lado, el bot, otra pestaña).
  useEffect(() => {
    if (!open) return;
    return onDataChanged(() => fetchDeudas(), 'deudas');
  }, [open, fetchDeudas]);

  function abrirNuevo() { setEditing(null); setModalOpen(true); }
  function abrirEdicion(d) { setEditing(d); setModalOpen(true); }

  function abrirPago(d) {
    setPagoDeuda(d);
    setMontoPago('');
    setFechaPago(hoyStr());
  }

  async function guardarPago() {
    if (!montoPago || parseFloat(montoPago) <= 0) { showToast('Ingresá un monto válido', 'error'); return; }
    setGuardandoPago(true);
    const d = pagoDeuda;
    const monto = parseFloat(montoPago);
    const nuevoCobrado = (d.monto_cobrado || 0) + monto;
    await supabase.from('deudas_pagos').insert({ deuda_id: d.id, monto, fecha: fechaPago || null });
    await supabase.from('deudas').update({ monto_cobrado: nuevoCobrado, estado: nuevoCobrado >= d.monto_total ? 'cobrada' : d.estado }).eq('id', d.id);
    await supabase.from('ingresos').insert({
      monto, fecha: fechaPago || null,
      concepto: `Pago de deuda: ${d.concepto}`,
      cliente_id: d.cliente_id || null,
      deuda_id: d.id, medio_cobro: 'Efectivo',
    });
    setGuardandoPago(false);
    setPagoDeuda(null);
    showToast('Cobro registrado ✓ — se generó el ingreso');
    fetchDeudas();
    onSaved?.();
  }

  async function eliminar(d) {
    if (!window.confirm('¿Eliminar esta deuda?')) return;
    // RPC SECURITY DEFINER: el UPDATE directo desde el cliente fallaba en
    // silencio por el WITH CHECK de RLS (mismo patrón que tramites).
    const { error } = await supabase.rpc('eliminar_deuda', { p_deuda_id: d.id });
    if (error) { showToast('Error al eliminar', 'error'); return; }
    showToast('Deuda enviada a la papelera', 'error');
    fetchDeudas();
    onSaved?.();
  }

  function handleSaved() { fetchDeudas(); onSaved?.(); }

  if (!open) return null;

  const visibles = estadoFiltro === 'todas'
    ? deudas
    : estadoFiltro === 'activas'
      ? deudas.filter(d => d.estado !== 'cobrada' && d.estado !== 'incobrable')
      : deudas.filter(d => d.estado === estadoFiltro);

  const saldoPendiente = visibles.reduce((s, d) => s + Math.max(0, (d.monto_total || 0) - (d.monto_cobrado || 0)), 0);

  return (
    <>
      <div className="drawer-overlay" onMouseDown={onClose} />
      <div className="drawer drawer-wide">

        <div className="drawer-header">
          <div className="drawer-header-top">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div>
                <div className="drawer-num">CONTABILIDAD</div>
                <div className="drawer-title">Deudas a cobrar</div>
              </div>
              <button
                onClick={() => setOcultarLocal(v => !v)}
                title={ocultarLocal ? 'Mostrar cifras' : 'Ocultar cifras'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, padding: 4, color: 'var(--text4)', display: 'flex', alignItems: 'center' }}
              >
                {ocultarLocal ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
              </button>
            </div>
            <button className="drawer-close" onClick={onClose}>✕</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', gap: 18 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Saldo pendiente</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>{formatMoney(saldoPendiente)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Deudas</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{visibles.length}</div>
              </div>
            </div>
            <button className="btn btn-primary" onClick={abrirNuevo}>+ Nueva deuda</button>
          </div>
        </div>

        <div className="drawer-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
              {{ activas: 'Activas', vigente: 'Vigentes', 'en gestión': 'En gestión', cobrada: 'Cobradas', incobrable: 'Incobrables', todas: 'Todas' }[estadoFiltro]}
              <span style={{ color: 'var(--text4)', fontWeight: 400 }}> · {visibles.length}</span>
            </span>
            <select className="form-select" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} style={{ width: 'auto', fontSize: 12, padding: '5px 10px' }}>
              <option value="activas">Activas</option>
              <option value="vigente">Vigentes</option>
              <option value="en gestión">En gestión</option>
              <option value="cobrada">Cobradas</option>
              <option value="incobrable">Incobrables</option>
              <option value="todas">Todas</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>Cargando…</div>
          ) : visibles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>Sin deudas en esta vista.</div>
          ) : (
            <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visibles.map(d => {
                const nombre = d.clientes ? `${d.clientes.apellido || ''} ${d.clientes.nombre || ''}`.trim() : d.deudor_nombre || '—';
                const saldo = Math.max(0, (d.monto_total || 0) - (d.monto_cobrado || 0));
                const pct = d.monto_total > 0 ? Math.min(100, ((d.monto_cobrado || 0) / d.monto_total) * 100) : 0;
                const eColor = ESTADO_COLOR[d.estado] || '#9ca3af';
                const cobrable = d.estado !== 'cobrada' && d.estado !== 'incobrable';
                return (
                  <div key={d.id} style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }} onClick={() => abrirEdicion(d)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.concepto}</div>
                        <div style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                          <span>{nombre}</span>
                          {d.fecha_vencimiento && <><span>·</span><span>vence {formatDate(d.fecha_vencimiento)}</span></>}
                          <span style={{ background: eColor + '22', color: eColor, padding: '0 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{d.estado}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 700, color: saldo > 0 ? '#f59e0b' : '#22c55e' }}>{formatMoney(saldo)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text4)' }}>de {formatMoney(d.monto_total)}</div>
                      </div>
                    </div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', margin: '8px 0' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22c55e' : '#3b82f6', borderRadius: 2 }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      {cobrable && (
                        <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); abrirPago(d); }} style={{ fontSize: 11, color: 'var(--green)' }}>Registrar cobro</button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); abrirEdicion(d); }} style={{ fontSize: 11 }}>Editar</button>
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); eliminar(d); }} style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', alignItems: 'center' }} title="Eliminar"><TrashIcon /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {toast && (
          <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', background: toast.tipo === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.tipo === 'error' ? '#b91c1c' : '#15803d', border: `0.5px solid ${toast.tipo === 'error' ? '#fecaca' : '#bbf7d0'}`, padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            {toast.msg}
          </div>
        )}
      </div>

      <DeudaModal open={modalOpen} editing={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} />

      {/* Modal registrar cobro */}
      {pagoDeuda && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setPagoDeuda(null)}>
          <div className="modal" style={{ width: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Registrar cobro</div>
              <button className="modal-close" onClick={() => setPagoDeuda(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--text4)', marginBottom: 12 }}>
                {pagoDeuda.concepto} · pendiente <strong style={{ color: 'var(--amber)' }}>{formatMoney(Math.max(0, (pagoDeuda.monto_total || 0) - (pagoDeuda.monto_cobrado || 0)))}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Monto cobrado ($)</label>
                  <MoneyInput value={montoPago} onChange={v => setMontoPago(v)} autoFocus />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Fecha del cobro</label>
                  <input className="form-input" type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 8 }}>
                Se descuenta del saldo de la deuda y se genera automáticamente un ingreso.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPagoDeuda(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarPago} disabled={guardandoPago}>{guardandoPago ? 'Guardando…' : 'Registrar cobro'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
